import { useDataModel } from '@thd-nucleus/data-sources';
import {
  SPECS_BATCH_SIZE,
  fitsDimensions,
  getProductDimensions,
  getSizeRefinedNavParam,
  parseUserDimensions,
  sortBySimilarity
} from './fitOptions';

/**
 * Products for the fitOptions flow.
 * 1. With userDimensions: read the base navParam's size facets and add the buckets that can fit
 *    (fitOptionsSizeFacets). Without them, search the navParam as is.
 * 2. Search the (refined) navParam (searchModel), dropping the current item and discontinued items.
 * 3. Fetch specifications for the results and the current item (fitOptionsProductSpecs). With
 *    userDimensions, keep only products whose exact dimensions fit.
 * 4. Sort by similarity to the current item: same finish, then closest price, then same brand.
 */
export const useFitOptionsProducts = ({
  itemId, storeId, navParam, userDimensions, fetch, skip
}) => {
  const maxDimensions = parseUserDimensions(userDimensions);
  const filterBySize = Boolean(maxDimensions);
  const skipAll = skip || !fetch || !navParam;

  const facets = useDataModel('fitOptionsSizeFacets', {
    variables: {
      navParam,
      storeId,
      storefilter: 'ALL'
    },
    ssr: false,
    skip: skipAll || !filterBySize
  });

  let searchNavParam = navParam;
  if (filterBySize) {
    // If the facets can't be read, search unscoped; the exact dimension check still applies
    searchNavParam = facets.error
      ? navParam
      : facets.data && getSizeRefinedNavParam({
        navParam,
        dimensions: facets.data.searchModel?.dimensions,
        maxDimensions
      });
  }
  const nothingFits = filterBySize && Boolean(facets.data) && searchNavParam === null;

  const search = useDataModel('searchModel', {
    variables: {
      navParam: searchNavParam,
      storeId,
      storefilter: 'ALL'
    },
    ssr: false,
    skip: skipAll || !searchNavParam
  });

  const candidates = search.data?.searchModel?.products?.filter(
    (product) => product?.itemId !== itemId && !product?.availabilityType?.discontinued
  );
  // The current item goes first so its finish, price and brand are available for sorting. Default
  // search page size is 24, so the current item plus a page fits in two batches.
  const candidateIds = (candidates || []).map((product) => product.itemId);
  const specsItemIds = candidateIds.length ? [itemId, ...candidateIds] : [];
  const firstBatch = specsItemIds.slice(0, SPECS_BATCH_SIZE);
  const secondBatch = specsItemIds.slice(SPECS_BATCH_SIZE, SPECS_BATCH_SIZE * 2);

  const firstSpecs = useDataModel('fitOptionsProductSpecs', {
    variables: { itemIds: firstBatch, storeId },
    ssr: false,
    skip: skipAll || !firstBatch.length
  });
  const secondSpecs = useDataModel('fitOptionsProductSpecs', {
    variables: { itemIds: secondBatch, storeId },
    ssr: false,
    skip: skipAll || !secondBatch.length
  });

  const loading = facets.loading || search.loading || firstSpecs.loading || secondSpecs.loading;
  const error = search.error || firstSpecs.error || secondSpecs.error;

  // Without a navParam there is nothing to search; render the empty state rather than nothing
  if (!navParam) {
    return { hasResponse: fetch, products: undefined, loading: false, error: undefined };
  }

  if (nothingFits) {
    return { hasResponse: true, products: [], loading: false, error: undefined };
  }

  const specsDone = (!firstBatch.length || Boolean(firstSpecs.data || firstSpecs.error))
    && (!secondBatch.length || Boolean(secondSpecs.data || secondSpecs.error));
  const hasResponse = Boolean(search.data) && specsDone;
  if (!hasResponse) {
    return {
      hasResponse, products: undefined, loading, error
    };
  }

  const specsProducts = [...(firstSpecs.data?.products || []), ...(secondSpecs.data?.products || [])];
  const specificationGroups = specsProducts
    .reduce((acc, product) => ({ ...acc, [product?.itemId]: product?.specificationGroup }), {});
  const current = specsProducts.find((product) => product?.itemId === itemId);
  const currentItem = current && {
    specificationGroup: current.specificationGroup,
    price: current.pricing?.value,
    brandName: current.identifiers?.brandName
  };

  // Products beyond the second batch (or without specs) can't be verified, so they're left out
  const fitting = filterBySize
    ? (candidates || []).filter((product) => specsItemIds.indexOf(product.itemId) < SPECS_BATCH_SIZE * 2
      && fitsDimensions(getProductDimensions(specificationGroups[product.itemId]), maxDimensions))
    : candidates;

  // Search results have no specifications; attach the fetched ones so the pods can show dimensions
  const withSpecifications = fitting?.map((product) => ({
    ...product,
    specificationGroup: specificationGroups[product.itemId] || product.specificationGroup
  }));

  return {
    hasResponse,
    products: withSpecifications && sortBySimilarity(withSpecifications, { specificationGroups, currentItem }),
    loading,
    error
  };
};
